import type { IImageProps } from './types';
import { shouldLoadEagerly } from './stores/loading';
import { useEagerLoadingContext } from '@klv/context/eager-loading';

// Detect if we're in Gatsby environment
const isGatsbyEnvironment = () => {
  // Use the GENERATOR environment variable set by build configs
  return process.env.GENERATOR === 'gatsby';
};

// Internal component that handles the actual rendering
const ImageInternal = ({
  altText = '',
  imageData,
  isDecorative = false,
  width,
  height,
  sizes = '100vw',
  loading,
  mediaDetails,
  sourceUrl,
  srcSet,
  ...props
}: IImageProps) => {
  const a11yHidden = isDecorative || !altText;
  // Normalize image data to common format - support both IMediaItem and IGatsbyImageData
  // Add safety check to prevent 'in' operator error when imageData is undefined
  const normalizedImageData = imageData && 'sourceUrl' in imageData
    ? {
      src: imageData.sourceUrl,
      srcSet: imageData.srcSet,
      sizes: sizes,
      alt: altText || imageData.altText || '',
      width: imageData.mediaDetails?.width,
      height: imageData.mediaDetails?.height,
    }
    : imageData
    ? {
      src: (imageData as any).images?.fallback?.src || '',
      srcSet: (imageData as any).images?.fallback?.srcSet || (imageData as any).images?.sources?.[0]?.srcSet || '',
      sizes: (imageData as any).images?.fallback?.sizes || (imageData as any).images?.sources?.[0]?.sizes || sizes,
      alt: altText,
      width: (imageData as any).width,
      height: (imageData as any).height,
    }
    : {
      src: '',
      srcSet: '',
      sizes: sizes,
      alt: altText,
      width: undefined,
      height: undefined,
    };

  const imageWidth = width || normalizedImageData.width;
  const imageHeight = height || normalizedImageData.height;

  return (
    <img
      {...props}
      src={normalizedImageData.src}
      srcSet={normalizedImageData.srcSet}
      sizes={normalizedImageData.sizes}
      alt={a11yHidden ? '' : normalizedImageData.alt}
      loading={loading}
      {...(a11yHidden && { 'aria-hidden': true })}
      {...(imageWidth && { width: imageWidth })}
      {...(imageHeight && { height: imageHeight })}
      style={{
        maxWidth: '100%',
        width: '100%',
        height: 'auto',
        ...props.style,
      }}
    />
  );
};

// Create two separate components to avoid React hooks rule violations
const ImageWithContext = ({ loading, ...props }: IImageProps) => {
  // This component will only be used in Gatsby where context is available
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const contextLoading = useEagerLoadingContext();
  const effectiveLoading = loading || contextLoading || 'lazy';

  return <ImageInternal {...props} loading={effectiveLoading} />;
};

const ImageWithNanostore = ({ loading, ...props }: IImageProps) => {
  // This component will be used in Astro or when context is not available
  const shouldLoadEagerlyValue = shouldLoadEagerly();
  const effectiveLoading = loading || (shouldLoadEagerlyValue ? 'eager' : 'lazy');

  return <ImageInternal {...props} loading={effectiveLoading} />;
};

// Main Image component that chooses the right implementation
const Image = (props: IImageProps) => {
  // Use environment detection to choose the right component
  if (isGatsbyEnvironment()) {
    return <ImageWithContext {...props} />;
  } else {
    return <ImageWithNanostore {...props} />;
  }
};

export default Image;
